import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CATEGORY_ICONS } from '@/lib/categoryIcons';
import { useLanguage } from '@/contexts/LanguageContext';

const PREDEFINED_CATEGORIES = [
  'Food',
  'Transport',
  'Shopping',
  'Fun',
  'Health',
  'Housing',
  'Bills/Other',
];

interface CategoryChipProps {
  value: string;
  onChange: (category: string) => void;
  className?: string;
}

export const CategoryChip = ({ value, onChange, className }: CategoryChipProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useLanguage();

  const icon = CATEGORY_ICONS[value] || '📝';

  const handleSelect = (category: string) => {
    onChange(category);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg',
            'border border-dashed border-muted-foreground/40 hover:border-primary/60 bg-muted/30 hover:bg-muted/50',
            'transition-all cursor-pointer group min-h-[36px] touch-manipulation',
            className
          )}
          aria-label={t('chat.editCategory')}
        >
          <span>{icon}</span>
          <span>{value}</span>
          <ChevronDown className="w-3 h-3 text-muted-foreground group-hover:text-primary opacity-60 group-hover:opacity-100 transition-opacity" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="space-y-1">
          {PREDEFINED_CATEGORIES.map((category) => (
            <button
              key={category}
              onClick={() => handleSelect(category)}
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left',
                'hover:bg-muted transition-colors',
                value === category && 'bg-primary/10 text-primary font-medium'
              )}
            >
              <span>{CATEGORY_ICONS[category] || '📝'}</span>
              <span>{category}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
